import { CanceledError } from 'axios';
import { useCallback, useRef, useState } from 'react';
import { useLatest } from 'react-use';

export interface UseSuggestionsReturn<TSuggestion extends any> {
  suggestions: TSuggestion[];
  onGetSuggestions: (value: string) => Promise<void>;
  onClearSuggestions: () => void;
}

export const useSuggestions = <TSuggestion extends any>(
  fetchSuggestions: (value: string, abortSignal: AbortSignal) => Promise<TSuggestion[]>
): UseSuggestionsReturn<TSuggestion> => {
  const abortRef = useRef<AbortController>();
  const fetchRef = useLatest(fetchSuggestions);

  const [suggestions, setSuggestions] = useState<TSuggestion[]>([]);

  const onGetSuggestions = useCallback(async (value: string) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    try {
      const response = await fetchRef.current(value, abortRef.current.signal);

      abortRef.current = null;
      setSuggestions(response);
    } catch (err) {
      // Ignore cancelled errors - this is just the abort signal doing it's thing.
      if (!(err instanceof CanceledError)) {
        throw err;
      }
    }
  }, []);

  const onClearSuggestions = useCallback(() => setSuggestions([]), []);

  return {
    suggestions,
    onGetSuggestions,
    onClearSuggestions,
  };
};
